/**
 * Survey List Component
 * Display and manage surveys
 */

import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, Send, Search, X } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { getSurveys, deleteSurvey } from '../../services/surveyAPI';
import Toast from './Toast';
import DeleteConfirmModal from './DeleteConfirmModal';
import SurveyPreview from './SurveyPreview';
import SurveyDistributionPreview from './SurveyDistributionPreview';
import PaginationControls from './PaginationControls';
import { useNavigate } from 'react-router-dom';

const SurveyList = () => {
  const [surveys, setSurveys] = useState([]);
  const [filteredSurveys, setFilteredSurveys] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);
  const [distributionModal, setDistributionModal] = useState(null);
  const navigate = useNavigate();
  
  const ITEMS_PER_PAGE = 15;

  useEffect(() => {
    loadSurveys();
  }, []);

  // Apply client-side filtering whenever surveys or search changes
  useEffect(() => {
    let filtered = [...surveys];
    
    // Apply search filter (case-insensitive, searches cohort name)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(survey => 
        (survey.cohort_name || '').toLowerCase().includes(query)
      );
    }
    
    setFilteredSurveys(filtered);
    setCurrentPage(1); // Reset to first page when filter changes
  }, [surveys, searchQuery]);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      const response = await getSurveys();
      // Handle both response formats: { surveys: [] } and { data: { surveys: [] } }
      const surveyList = response.data?.surveys || response.surveys || [];
      console.log('Loaded surveys:', surveyList);
      if (surveyList.length > 0) {
        console.log('First survey structure:', surveyList[0]);
      }
      setSurveys(surveyList);
    } catch (error) {
      console.error('Error loading surveys:', error);
      showToast('Failed to load surveys', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleDelete = async (surveyId) => {
    console.log('=== DELETE SURVEY DEBUG ===');
    console.log('Survey ID to delete:', surveyId);
    console.log('Survey ID type:', typeof surveyId);
    
    try {
      const result = await deleteSurvey(surveyId);
      console.log('Delete result:', result);
      showToast('Survey deleted successfully!');
      await loadSurveys();
    } catch (error) {
      console.error('=== DELETE ERROR ===');
      console.error('Error object:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete survey';
      showToast(errorMessage, 'error');
    } finally {
      setDeleteModal(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      DRAFT: { bg: '#f3f4f6', color: '#6b7280', text: 'Draft' },
      ACTIVE: { bg: '#dbeafe', color: '#1e40af', text: 'Active' },
      COMPLETED: { bg: '#d1fae5', color: '#065f46', text: 'Completed' },
      ARCHIVED: { bg: '#fef3c7', color: '#92400e', text: 'Archived' }
    };
    const style = styles[status] || styles.DRAFT;
    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        background: style.bg,
        color: style.color,
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: '700',
        textTransform: 'uppercase'
      }}>
        {style.text}
      </span>
    );
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredSurveys.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedSurveys = filteredSurveys.slice(startIndex, endIndex);

  return (
    <AdminLayout>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <div>
            <h1 style={{ 
              fontSize: '2rem', 
              fontWeight: '700', 
              color: '#111827',
              marginBottom: '0.5rem'
            }}>
              Surveys
            </h1>
            <p style={{ color: '#6b7280', fontSize: '1rem', margin: 0 }}>
              Manage leadership competency surveys
            </p>
          </div>
          <button
            onClick={() => navigate('/leading/admin/surveys/new')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(99, 102, 241, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.25)';
            }}
          >
            <Plus size={18} />
            <span>Create Survey</span>
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ position: 'relative', maxWidth: '600px' }}>
            <Search 
              size={18} 
              style={{ 
                position: 'absolute', 
                left: '0.875rem', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#9ca3af',
                pointerEvents: 'none'
              }} 
            />
            <input
              type="text"
              placeholder="Search by cohort name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 3rem 0.75rem 2.75rem',
                background: 'white',
                border: '2px solid #e5e7eb',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                color: '#111827',
                transition: 'all 0.2s ease'
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#6b7280',
                  transition: 'all 0.2s ease'
                }}
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <div className="spinner" style={{ margin: '0 auto' }}></div>
            <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading surveys...</p>
          </div>
        ) : filteredSurveys.length === 0 ? (
          <div style={{
            background: '#ffffff',
            border: '1px dashed #d1d5db',
            borderRadius: '0.75rem',
            padding: '4rem 2rem',
            textAlign: 'center'
          }}>
            <p style={{ color: '#6b7280', fontSize: '1rem', marginBottom: '1rem' }}>
              {searchQuery ? 'No surveys match your search' : 'No surveys created yet'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => navigate('/leading/admin/surveys/new')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Create Your First Survey
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '0.75rem',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase' }}>
                      Cohort
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase' }}>
                      Type
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase' }}>
                      Questions
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase' }}>
                      Status
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase' }}>
                      Created
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSurveys.map((survey) => (
                    <tr key={survey.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
                          {survey.cohort_name || `Cohort ${survey.cohort_id}`}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {survey.response_window} day window
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '0.375rem',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          {survey.survey_type}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', color: '#374151' }}>
                        {(Array.isArray(survey.questions) ? survey.questions : JSON.parse(survey.questions || '[]')).length} questions
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {getStatusBadge(survey.status)}
                      </td>
                      <td style={{ padding: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                        {new Date(survey.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => setPreviewModal(survey)}
                            style={{
                              padding: '0.5rem',
                              background: '#f3f4f6',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
                            title="Preview"
                          >
                            <Eye size={16} color="#374151" />
                          </button>
                          <button
                            onClick={() => navigate(`/leading/admin/surveys/edit/${survey.id}`)}
                            style={{
                              padding: '0.5rem',
                              background: '#dbeafe',
                              border: '1px solid #93c5fd',
                              borderRadius: '0.375rem',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#bfdbfe'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#dbeafe'}
                            title="Edit"
                          >
                            <Edit2 size={16} color="#1e40af" />
                          </button>
                          {survey.status === 'DRAFT' && (
                            <button
                              onClick={() => {
                                console.log('Send button clicked for survey:', survey);
                                console.log('Survey ID:', survey.id);
                                console.log('Survey keys:', Object.keys(survey));
                                setDistributionModal(survey);
                              }}
                              style={{
                                padding: '0.5rem',
                                background: '#d1fae5',
                                border: '1px solid #a7f3d0',
                                borderRadius: '0.375rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#a7f3d0'}
                              onMouseLeave={(e) => e.currentTarget.style.background = '#d1fae5'}
                              title="Send Survey"
                            >
                              <Send size={16} color="#065f46" />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              console.log('Delete button clicked for survey:', survey);
                              console.log('Survey ID:', survey.id);
                              setDeleteModal(survey);
                            }}
                            style={{
                              padding: '0.5rem',
                              background: '#fef2f2',
                              border: '1px solid #fecaca',
                              borderRadius: '0.375rem',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#fef2f2'}
                            title="Delete"
                          >
                            <Trash2 size={16} color="#dc2626" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                total={filteredSurveys.length}
                limit={ITEMS_PER_PAGE}
                hasNext={currentPage < totalPages}
                hasPrev={currentPage > 1}
                onPageChange={(newPage) => {
                  setCurrentPage(newPage);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                itemType="surveys"
              />
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      {deleteModal && (
        <DeleteConfirmModal
          isOpen={true}
          title="Delete Survey"
          message={`Are you sure you want to delete this survey? This action cannot be undone.`}
          onConfirm={() => handleDelete(deleteModal.id)}
          onClose={() => setDeleteModal(null)}
        />
      )}

      {previewModal && (
        <SurveyPreview
          survey={previewModal}
          onClose={() => setPreviewModal(null)}
          onSend={() => {
            setPreviewModal(null);
            setDistributionModal(previewModal);
          }}
        />
      )}

      {distributionModal && (
        <SurveyDistributionPreview
          survey={distributionModal}
          onClose={() => setDistributionModal(null)}
          onConfirm={() => {
            setDistributionModal(null);
            showToast('Survey sent successfully!');
            loadSurveys();
          }}
        />
      )}
    </AdminLayout>
  );
};

export default SurveyList;